import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DeliverySelectionMap from '@/components/delivery/DeliverySelectionMap';
import { PickupBranchCard } from '@/features/orders/components/PickupBranchCard';
import { useDeliveryOrder } from '@/features/orders/useDeliveryOrder';
import {
  fetchCargoBranches,
  saveDeliverySelection,
  type CargoBranch,
} from '@/data/cargo.api';

export default function DeliverySelectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { language } = useTheme();
  const [search, setSearch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<CargoBranch | null>(null);
  const deferredSearch = useDeferredValue(search);

  const orderQuery = useDeliveryOrder(id);

  const branchesQuery = useQuery({
    queryKey: ['cargo-branches'],
    queryFn: () => fetchCargoBranches(),
  });

  useEffect(() => {
    const order = orderQuery.data;
    if (!order || !id) return;

    if (order.paymentStatus !== 'PAID') {
      navigate(`/orders/${id}`, { replace: true });
      return;
    }

    if (
      order.cargoBranchId &&
      ['SELECTED', 'LOCKED', 'OVERRIDDEN_BY_ADMIN'].includes(order.deliverySelectionStatus || '')
    ) {
      navigate(`/orders/${id}/confirmation`, { replace: true });
    }
  }, [id, navigate, orderQuery.data]);

  useEffect(() => {
    if (!branchesQuery.data?.length) {
      setSelectedBranch(null);
      return;
    }

    if (selectedBranch && branchesQuery.data.some((branch) => branch.id === selectedBranch.id)) {
      return;
    }

    setSelectedBranch(branchesQuery.data[0]);
  }, [branchesQuery.data, selectedBranch]);

  const selectionMutation = useMutation({
    mutationFn: (cargoBranchId: string) => saveDeliverySelection(id!, cargoBranchId),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order', id] }),
        queryClient.invalidateQueries({ queryKey: ['cargo-branches'] }),
      ]);
      toast.success(language === 'mn' ? 'Ачилтын салбар хадгалагдлаа' : 'Pickup branch saved');
      navigate(`/orders/${id}/confirmation`, { replace: true, state: { order: result.order } });
    },
    onError: (error: Error) => {
      toast.error(error.message);
      queryClient.invalidateQueries({ queryKey: ['cargo-branches'] });
    },
  });

  const searchResults = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    if (!term || !branchesQuery.data?.length) return [];

    return branchesQuery.data
      .filter((branch) =>
        [
          branch.name,
          branch.code,
          branch.city,
          branch.district,
          branch.addressLine1,
          branch.addressLine2,
          branch.landmark,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term)),
      )
      .slice(0, 8);
  }, [branchesQuery.data, deferredSearch]);

  const showSearchResults = deferredSearch.trim().length > 0;

  if (orderQuery.isLoading || branchesQuery.isLoading) {
    return (
      <div className="container mx-auto min-h-screen px-4 py-32">
        <div className="mx-auto max-w-2xl rounded-2xl border bg-card p-10 text-center shadow-sm">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-muted-foreground">
            {language === 'mn' ? 'Салбарын мэдээлэл ачааллаж байна...' : 'Loading pickup branches...'}
          </p>
        </div>
      </div>
    );
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <div className="container mx-auto min-h-screen px-4 py-32">
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <CardTitle>{language === 'mn' ? 'Захиалга ачаалж чадсангүй' : 'Unable to load order'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/orders">{language === 'mn' ? 'Захиалгууд руу буцах' : 'Back to orders'}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-background pt-24 pb-12">
      <div className="container mx-auto max-w-7xl px-4">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-medium text-primary">
              {language === 'mn' ? 'Төлбөр амжилттай' : 'Payment successful'}
            </p>
            <h1 className="text-3xl font-bold">
              {language === 'mn' ? 'Cargo салбараа сонгоно уу' : 'Select your pickup branch'}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {language === 'mn'
                ? 'Захиалгыг баталгаажуулахын өмнө ачилтын салбар заавал сонгоно.'
                : 'Choose a cargo pickup branch before we finalize your order.'}
            </p>
          </div>
          <Card className="w-full md:w-[280px]">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">{language === 'mn' ? 'Захиалгын дугаар' : 'Order'}</p>
              <p className="text-lg font-semibold">{orderQuery.data.id.slice(0, 8).toUpperCase()}</p>
              <p className="mt-3 text-sm text-muted-foreground">{language === 'mn' ? 'Нийт төлбөр' : 'Total paid'}</p>
              <p className="text-2xl font-bold text-primary">₮{Number(orderQuery.data.total).toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.55fr_0.85fr] xl:grid-cols-[1.7fr_0.8fr]">
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>{language === 'mn' ? 'Газрын зураг ба салбарууд' : 'Map and branches'}</CardTitle>
              <CardDescription>
                {language === 'mn'
                  ? 'Жагсаалтаас эсвэл газрын зурган дээрх preview-аас салбараа сонгоно.'
                  : 'Pick a branch from the list and review its map preview.'}
              </CardDescription>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={language === 'mn' ? 'Салбар хайх...' : 'Search branches...'}
              />
              {showSearchResults && (
                <div className="rounded-xl border bg-background">
                  {searchResults.length > 0 ? (
                    <div className="max-h-56 overflow-y-auto py-1">
                      {searchResults.map((branch) => {
                        const isSelected = branch.id === selectedBranch?.id;
                        return (
                          <button
                            key={branch.id}
                            type="button"
                            onClick={() => {
                              setSelectedBranch(branch);
                              setSearch(branch.name);
                            }}
                            className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition ${
                              isSelected ? 'bg-primary/10' : 'hover:bg-accent/50'
                            }`}
                          >
                            <div>
                              <p className="font-medium">{branch.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {[branch.city, branch.district].filter(Boolean).join(' • ')}
                              </p>
                            </div>
                            {isSelected && (
                              <span className="text-xs font-semibold text-primary">
                                {language === 'mn' ? 'Сонгогдсон' : 'Selected'}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-muted-foreground">
                      {language === 'mn' ? 'Хайлтад тохирох салбар олдсонгүй.' : 'No matching branches found.'}
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              {branchesQuery.data && branchesQuery.data.length > 0 ? (
                <DeliverySelectionMap
                  branches={branchesQuery.data}
                  selectedBranchId={selectedBranch?.id}
                  onSelect={setSelectedBranch}
                />
              ) : (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">
                  {language === 'mn'
                    ? 'Одоогоор идэвхтэй cargo салбар алга. Дараа дахин оролдоно уу.'
                    : 'No active pickup branches are available right now.'}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="h-fit">
            <CardHeader>
              <CardTitle>{language === 'mn' ? 'Сонгосон салбар' : 'Selected branch'}</CardTitle>
              <CardDescription>
                {language === 'mn'
                  ? 'Доорх мэдээллийг шалгаад хадгална.'
                  : 'Review the selected branch details before saving.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedBranch ? (
                <>
                  <PickupBranchCard branch={selectedBranch} language={language} variant="panel" />

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => selectedBranch && selectionMutation.mutate(selectedBranch.id)}
                    disabled={selectionMutation.isPending || !selectedBranch}
                  >
                    {selectionMutation.isPending
                      ? (language === 'mn' ? 'Хадгалж байна...' : 'Saving...')
                      : (language === 'mn' ? 'Энэ салбарыг сонгох' : 'Confirm this branch')}
                  </Button>
                </>
              ) : (
                <div className="rounded-xl border border-dashed p-6 text-center text-muted-foreground">
                  {language === 'mn'
                    ? 'Жагсаалтаас cargo салбар сонгоно уу.'
                    : 'Select a cargo branch to continue.'}
                </div>
              )}

              <Button asChild variant="outline" className="w-full">
                <Link to={`/orders/${id}`}>{language === 'mn' ? 'Захиалгын дэлгэрэнгүй рүү буцах' : 'Back to order details'}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
